/**
 * i18n 골격 (Tier 0 — 2026-07-24 승격, 계획: company/docs/i18n/PLAN-i18n.md)
 *
 * 규약:
 *  - 기본어 ko (fallbackLng) — 한국 서비스가 1차 시장. 미번역 키는 ko 로 폴백된다
 *  - 네임스페이스: 도메인당 1개 JSON (locales/<lng>/<ns>.json). 템플릿은 common 만 제공 —
 *    프로젝트가 화면 도메인별로 추가한다 (예: shop.json, order.json)
 *  - 키 규칙: <그룹>.<의미> 카멜케이스 (예: action.confirm, error.network). 문장 전체를
 *    키로 쓰지 말 것(원문 변경 시 키가 흔들린다)
 *  - 언어 선택: 저장값(localStorage) > 기기 언어 > ko. 변경은 setAppLanguage() —
 *    설정 화면에서 호출하면 즉시 반영 + 저장된다
 *
 * 사용:
 *   const { t } = useTranslation();            // common (기본 ns)
 *   t("action.confirm")
 *   const { t } = useTranslation("shop");      // 프로젝트 ns
 */
import i18n from "i18next";
import { initReactI18next } from "react-i18next";

import enCommon from "./locales/en/common.json";
import jaCommon from "./locales/ja/common.json";
import koCommon from "./locales/ko/common.json";
// peeka 도메인 ns — 카드/스캔/컬렉션 화면 문자열
import enCard from "./locales/en/card.json";
import jaCard from "./locales/ja/card.json";
import koCard from "./locales/ko/card.json";
// 앱 전역 ns — 브랜드명이 들어가는 서비스 안내 문구(종료·탈퇴·선물·문의·웹 안내).
// 브랜드명은 common:brand.name 한 곳에만 두고 $t() 중첩으로 참조한다.
import enApp from "./locales/en/app.json";
import jaApp from "./locales/ja/app.json";
import koApp from "./locales/ko/app.json";
// peeka 도메인 ns — 내 정보(SC-005) 화면 문자열
import enMe from "./locales/en/me.json";
import enNotification from "./locales/en/notification.json";
import jaMe from "./locales/ja/me.json";
import jaNotification from "./locales/ja/notification.json";
import koMe from "./locales/ko/me.json";
import koNotification from "./locales/ko/notification.json";

const STORAGE_KEY = "app.language";

export const SUPPORTED_LANGUAGES = ["ko", "en", "ja"] as const;
export type AppLanguage = (typeof SUPPORTED_LANGUAGES)[number];

/**
 * 언어 선택 UI 의 옵션 라벨 — 각 언어의 자국어 표기(autonym)로 고정한다.
 * 번역 대상이 아니다: 실수로 영어로 바꾼 사용자가 되돌아오려면 "한국어" 가
 * 영어 화면에서도 "한국어" 로 보여야 한다.
 */
export const LANGUAGE_LABELS: Record<AppLanguage, string> = {
  ko: "한국어",
  en: "English",
  ja: "日本語",
};

export function isAppLanguage(value: string): value is AppLanguage {
  return (SUPPORTED_LANGUAGES as readonly string[]).includes(value);
}

/**
 * 처음 열었을 때의 언어 — 저장값 > 기기 언어 > **en**.
 *
 * ⚠ 마지막 폴백이 ko 가 아니라 en 이다(2026-09-15). 출시 국가가 한·일·미인데 종전에는
 *   프랑스어·중국어처럼 지원 목록 밖의 기기가 전부 한국어로 떨어졌다 — 그 사람들에게는
 *   영어가 읽힐 가능성이 훨씬 높다. app.peeka.app 소개 페이지가 공개되면서 실제로
 *   불특정 국가의 방문이 생기는 자리다.
 * ⚠ 번역 폴백(fallbackLng)은 ko 그대로다 — 그쪽은 "키가 비었을 때 정본으로"라는 다른 규칙이다.
 * (export 는 테스트용이다 — 앱 코드에서 직접 부를 일은 없다.)
 */
export function initialLanguage(): AppLanguage {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved && (SUPPORTED_LANGUAGES as readonly string[]).includes(saved)) {
      return saved as AppLanguage;
    }
  } catch {
    /* 저장소 접근 불가 — 기기 언어로 */
  }
  const device = (globalThis.navigator?.language ?? "").slice(0, 2).toLowerCase();
  return isAppLanguage(device) ? device : "en";
}

/**
 * 초기화 Promise (Codex): 리소스가 번들 내장이라 init 은 사실상 동기지만, 부트스트랩이
 * 완료를 보장하고 실패를 삼키지 않도록 export 한다 — main.tsx 가 렌더 전에 await.
 */
export const i18nReady: Promise<unknown> = i18n.use(initReactI18next).init({
  resources: {
    ko: { common: koCommon, card: koCard, app: koApp, me: koMe, notification: koNotification },
    en: { common: enCommon, card: enCard, app: enApp, me: enMe, notification: enNotification },
    ja: { common: jaCommon, card: jaCard, app: jaApp, me: jaMe, notification: jaNotification },
  },
  lng: initialLanguage(),
  fallbackLng: "ko",
  defaultNS: "common",
  interpolation: {
    escapeValue: false, // React 가 XSS 이스케이프를 담당
  },
  returnEmptyString: false, // 빈 번역은 키 노출 — 누락을 눈에 보이게
});
i18nReady.catch((e) => console.error("i18n init failed", e));

/**
 * 공개 URL(`?lang=ko|en|ja`)의 표시 언어를 정한다.
 *
 * `?lang=` 이 앱 언어보다 **우선**한다. 이 주소들은 스토어(Play 데이터 안전·App Store)와
 * OAuth 콘솔·광고에 등록돼 **남의 브라우저**에서 열리므로, 기기 언어에 맡기면 어느 언어가
 * 뜰지 우리가 통제할 수 없다. 일본 스토어에는 `?lang=ja` 를 거는 식으로 고정할 수 있다.
 *
 * 값이 없거나 지원 목록 밖이면 앱 언어(저장값 > 기기 언어 > en)를 따른다. `en-US` 처럼
 * 지역 태그가 붙어도 앞 2자로 본다.
 *
 * ⚠ 이 함수는 앱 언어 **설정을 바꾸지 않는다** — 공개 링크를 한 번 열었다고 사용자의
 *   저장된 언어가 바뀌면 안 된다. 쓰는 곳: 공개 약관(PublicTermsPage)·앱 소개(AppGuidePage).
 */
export function resolvePublicLanguage(param: string | null, appLanguage: AppLanguage): AppLanguage {
  const raw = param?.trim().toLowerCase().slice(0, 2);
  return raw && isAppLanguage(raw) ? raw : appLanguage;
}

/** 설정 화면 접합점 — 즉시 반영 + 저장 */
export async function setAppLanguage(lang: AppLanguage): Promise<void> {
  await i18n.changeLanguage(lang);
  try {
    localStorage.setItem(STORAGE_KEY, lang);
  } catch {
    /* 저장 실패는 무시 — 다음 실행에서 기기 언어로 */
  }
}

export function currentLanguage(): AppLanguage {
  // i18n.language 는 외부 changeLanguage 로 임의 값이 될 수 있다 — 무검증 캐스팅 금지 (Codex)
  const lang = i18n.language?.slice(0, 2);
  return lang && (SUPPORTED_LANGUAGES as readonly string[]).includes(lang) ? (lang as AppLanguage) : "ko";
}

export default i18n;
